class OrderStatus extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.output = this.querySelector('status-report');
    this.progressTemplate = this.querySelector('template[data-progress-steps]');
    this.error = this.querySelector('form .error');
    this.loadingStatus = this.querySelector('[data-tracking-status]');
    this.isLoading = false;
    this.onSubmit = (e) => this.submitForm(e);
    this.onReportToggle = (e) => this.handleReportToggle(e);
  }

  connectedCallback() {
    if (!this.form) {
      console.log("Unable to load, missing form element. Reload page and try again!");
      return;
    }

    this.form.addEventListener('submit', this.onSubmit);
    this.output?.addEventListener('toggle', this.onReportToggle, true);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.output?.removeEventListener('toggle', this.onReportToggle, true);
  }

  async submitForm(evt) {
    evt.preventDefault();
    if (this.isLoading) return;

    const formData = new FormData(evt.target);
    const region = this.getOrderRegion();
    formData.set('orderNumber', this.normalizeOrderNumber(formData.get('orderNumber'), region));
    formData.set('email', String(formData.get('email') || '').trim());
    formData.set('countryCode', region);

    const paramString = new URLSearchParams(formData).toString();
    const endpointURL = 'https://us-central1-mclellanhill-gcp.cloudfunctions.net/getOrderStatus';
    const button = this.form.querySelector('button[name="submit"]');
    const label = button.querySelector('[data-submit-label]');
    const idleLabel = label.textContent;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 45000);

    this.error.textContent = "";
    this.error.classList.add('hidden');
    this.isLoading = true;
    this.form.setAttribute('aria-busy', 'true');
    button.disabled = true;
    button.classList.add('btn--loading');
    label.textContent = 'Tracking order…';
    this.loadingStatus.textContent = 'Looking up your order. This may take a moment.';

    try {
      const request = await fetch(`${endpointURL}?${paramString}`, { signal: controller.signal });
      const responseText = await request.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch {
        throw new Error(request.status === 404 || responseText.trim() === 'Order Not Found'
          ? 'We couldn’t find an order with those details. Please check your order number and email, then try again.'
          : 'Unable to load order status. Please try again.');
      }

      if (!request.ok) {
        this.handleError(result);
        return;
      }

      this.renderReport(this.normalizeReport(result));
    } catch (error) {
      this.handleError(error.name === 'AbortError'
        ? { message: 'This is taking longer than expected. Please try tracking your order again.' }
        : error);
    } finally {
      clearTimeout(timeout);
      this.isLoading = false;
      this.form.removeAttribute('aria-busy');
      button.disabled = false;
      button.classList.remove('btn--loading');
      label.textContent = idleLabel;
      this.loadingStatus.textContent = '';
    }
  }

  getOrderRegion() {
    const regions = {
      'benchfoodsdehydrators.com.au': 'AU',
      'benchfoodsdehydrators.com': 'US',
      'benchfoodsdehydrators.ca': 'CA',
      'benchfoodsdehydrators.nz': 'NZ',
      'benchfoodsdehydrators.co.uk': 'UK',
      'benchfoodsdehydrators.eu': 'EU'
    };
    const host = window.location.hostname.replace(/^www\./, '');
    const country = String(window.Shopify?.country || 'AU').toUpperCase();
    return regions[host] || ({ GB: 'UK', UK: 'UK', AU: 'AU', US: 'US', CA: 'CA', NZ: 'NZ' }[country]) || 'EU';
  }

  normalizeOrderNumber(value, region) {
    const number = String(value || '').trim().replace(/^#\s*/, '');
    if (!number.includes('-')) return `${number}-${region}`;
    // Preserve an explicitly entered region, including when browsing a different storefront.
    return number.replace(/-(AU|US|CA|NZ|UK|EU)$/i, (_, suffix) => `-${suffix.toUpperCase()}`);
  }

  handleError(error) {
    this.error.textContent = error.message || error.description || 'Unable to load order status.';
    this.error.classList.remove('hidden');

    if (this.output) {
      this.output.innerHTML = '';
      this.output.setAttribute('hidden', true);
    }
  }

  normalizeReport(result) {
    if (Array.isArray(result)) {
      return result;
    }

    if (Array.isArray(result?.data)) {
      return result.data;
    }

    return result ? [result] : [];
  }

  escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return map[char] || char;
    });
  }

  buildProgressMarkup(stepNumber) {
    if (!this.progressTemplate) {
      return '';
    }

    const currentStep = Math.min(6, Math.max(0, Math.floor(Number(stepNumber) || 0)));
    return this.progressTemplate.innerHTML.replace(
      /animate-to="[^"]*"/,
      `animate-to="${currentStep}"`
    );
  }

  renderReport(data = []) {
    if (!Array.isArray(data) || !data.length) {
      this.handleError({ message: 'No order updates were found for these details.' });
      return;
    }

    const reportMarkup = data.map((order, index) => {
      const {
        address,
        wixOrderNumber,
        items = [],
        tracking_number,
        tracking_link,
        shipping_company,
        est_delivery_date,
        display_status_step
      } = order || {};

      const safeOrderNumber = this.escapeHtml(wixOrderNumber || 'N/A');
      const safeAddress = this.escapeHtml(address || 'N/A');
      const safeShipping = this.escapeHtml(shipping_company || 'N/A');
      const safeDate = this.escapeHtml(order.estimatedDeliveryDate || est_delivery_date || 'Pending');
      const safeTrackingNumber = this.escapeHtml(tracking_number || 'N/A');
      const summaryItems = items.map((item) => {
        const safeName = this.escapeHtml(item?.name || 'Item');
        const safeQty = this.escapeHtml(item?.qty || '1');
        const safePid = this.escapeHtml(item?.pid || '');
        return `
          <li>
            <p><strong>${safeName} x ${safeQty}</strong>${safePid ? `<small>${safePid}</small>` : ''}</p>
          </li>
        `;
      }).join('');

      const trackingMarkup = tracking_link
        ? `<a class="link" href="${this.escapeHtml(tracking_link)}" target="_blank" rel="noopener noreferrer">${safeTrackingNumber}</a>`
        : safeTrackingNumber;

      return `
        <details class="order-card" ${index === 0 ? 'open' : ''}>
          <summary>
            <h2 class="h5">${this.escapeHtml(this.output.dataset.title || 'Order')} ${safeOrderNumber}</h2>
          </summary>
          <div class="order-card__content">
            ${this.buildProgressMarkup(display_status_step)}
            <div class="summary">
              <h3 class="h6 mb-4">${this.escapeHtml(this.output.dataset.summaryHeading || 'Order Summary')}</h3>
              <ul>${summaryItems || '<li><p>N/A</p></li>'}</ul>
            </div>
            <div class="information">
              <h3 class="h6 mb-4">${this.escapeHtml(this.output.dataset.informationHeading || 'Order Information')}</h3>
              <ul>
                <li>
                  <h4 class="h6">${this.escapeHtml(this.output.dataset.deliveryAddressLabel || 'Delivery Address')}</h4>
                  <p>${safeAddress}</p>
                </li>
                <li>
                  <h4 class="h6">${this.escapeHtml(this.output.dataset.deliveryDateLabel || 'Estimated Delivery Date')}</h4>
                  <p>${safeDate}</p>
                </li>
                <li>
                  <h4 class="h6">${this.escapeHtml(this.output.dataset.shippingCompanyLabel || 'Shipping Company')}</h4>
                  <p>${safeShipping}</p>
                </li>
                <li>
                  <h4 class="h6">${this.escapeHtml(this.output.dataset.trackingNumberLabel || 'Tracking Number')}</h4>
                  <p>${trackingMarkup}</p>
                </li>
              </ul>
            </div>
          </div>
        </details>
      `;
    }).join('');

    this.output.innerHTML = reportMarkup;
    this.output.removeAttribute('hidden');
    this.error.innerHTML = '';
    this.error.classList.add('hidden');

    const allSteps = this.output.querySelectorAll('progress-steps');
    for (const steps of allSteps) {
      if (typeof steps.addAnimationClass === 'function') {
        steps.addAnimationClass(steps.getAttribute('animate-to'));
      }
    }
  }

  handleReportToggle(event) {
    const detailsEl = event.target;
    if (!(detailsEl instanceof HTMLDetailsElement) || !detailsEl.open) {
      return;
    }

    const cards = this.output?.querySelectorAll('details.order-card') || [];
    for (const card of cards) {
      if (card !== detailsEl) {
        card.open = false;
      }
    }
  }
}

class ProgressSteps extends HTMLElement {
  static observedAttributes = ["animate-to"];

  constructor() {
    super();
  }

  connectedCallback() {
    // Attribute callbacks can run before the parser has added the step elements.
    this.addAnimationClass(this.getAttribute('animate-to'));
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.addAnimationClass(newValue);
  }

  addAnimationClass(stepNumber) {
    if (Number.isNaN(Number(stepNumber))) {
      console.log("Attribute must be a number. Try again");
    }

    const current = +stepNumber;
    this.setAttribute('role', 'group');
    this.setAttribute('aria-label', current > 0 ? `Order progress: step ${current} of 6` : 'Order progress');
    const elems = this.querySelectorAll('.step-item');

    // Initially remove animation class to all element
    for (const elem of elems) {
      elem.classList.remove('animate');
    }

    // Add animation class to elements that passed condition
    for (const elem of elems) {
      const step = +elem.dataset.step;
      if (step === current) elem.setAttribute('aria-current', 'step');
      else elem.removeAttribute('aria-current');
      if (step <= current) {
        elem.classList.add('animate');
      }
    }
  }
}

if (!customElements.get('progress-steps')) {
  customElements.define('progress-steps', ProgressSteps)
}

if (!customElements.get('order-status')) {
  customElements.define('order-status', OrderStatus);
}
